import React, { useEffect, useRef, useState } from "react";
import {
  useGame,
  usePlayer,
  useStage,
} from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { getCounterpartCopy } from "../payoffFrame";

export function NegotiationChat() {
  const game = useGame();
  const player = usePlayer();
  const stage = useStage();
  const [text, setText] = useState("");
  const scroller = useRef(null);

  if (!game || !player) {
    return null;
  }

  const msgs = stage?.getAttribute("chat")?.items || [];
  const otherParticipantLabel = getCounterpartCopy(
    game.get("design")
  ).capitalizedTerm;

  useEffect(() => {
    if (!scroller.current) {
      return;
    }
    scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [msgs.length]);

  function handleSend(event) {
    event?.preventDefault();

    if (!stage) {
      return;
    }

    const trimmed = text.trim();
    if (!trimmed) {
      return;
    }

    stage.append("chat", {
      text: trimmed,
      sender: {
        id: player.id,
      },
    });
    setText("");
  }

  return (
    <div className="h-full w-full flex flex-col">
      <div ref={scroller} className="flex-1 overflow-auto px-3 py-3 space-y-3">
        {msgs.length === 0 ? (
          <div className="h-full w-full flex flex-col items-center justify-center text-gray-500 text-xl">
            Chat is available during negotiation.
          </div>
        ) : (
          msgs.map((msg, index) => {
            const msgValue = msg?.value || msg;
            const sender = msgValue?.sender || {};
            const isSelf = sender?.id === player.id;
            const prevValue =
              index > 0 ? (msgs[index - 1]?.value || msgs[index - 1]) : null;
            const prevSender = prevValue?.sender || {};
            const isSameSender = prevSender?.id === sender?.id;
            const displayName = isSelf ? "You" : otherParticipantLabel;

            return (
              <div
                key={msg.id || `${msg?.sender?.id}-${index}`}
                className={`flex ${isSelf ? "justify-end" : "justify-start"}`}
              >
                <div className="max-w-[85%]">
                  {!isSameSender && (
                    <div
                      className={`text-xl uppercase tracking-wide mb-1 ${
                        isSelf ? "text-empirica-500 text-right" : "text-gray-400"
                      }`}
                    >
                      {displayName}
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-4 py-2 text-xl whitespace-pre-wrap break-words [overflow-wrap:anywhere] shadow-sm ${
                      isSelf
                        ? "bg-empirica-600 text-white"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {msgValue?.text}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form className="border-t border-gray-200 p-3" onSubmit={handleSend}>
        <div className="flex items-center space-x-2">
          <input
            className="flex-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-full shadow-sm placeholder-gray-400 focus:outline-none focus:ring-empirica-500 focus:border-empirica-500 text-xl"
            type="text"
            placeholder="Type a message..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
                handleSend(event);
              }
            }}
          />
          <Button type="submit" primary>
            Send
          </Button>
        </div>
      </form>
    </div>
  );
}
